import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  TextField,
  Theme,
  useTheme,
} from "@mui/material";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import SelectAccessLevel from "pages/Users/SelectAccessLevel";
import { Field, withTypes } from "react-final-form";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import {
  OrganizationUserRequest,
  ResponseCode,
} from "services/organizationPortalClient";
import { CustomDialogProps } from "types/commonTypes";
import translateError from "utils/translateError";
import { isEmail } from "@knowfully/ui.common";

const useStyles = (theme: Theme) => ({
  title: {
    color: theme.palette.primary.dark,
  },
});

const USER_EXISTS_RESPONSE_REASON = "User exists";

export default function AddUserDialog({
  open,
  onClose,
}: Readonly<CustomDialogProps>) {
  const styles = useStyles(useTheme());
  const { formatMessage: t } = useIntl();
  const { organizationUserService } = useGlobalContext();
  const { Form } = withTypes<OrganizationUserRequest>();
  const init = {} as OrganizationUserRequest;
  const queryClient = useQueryClient();

  const submit = async (values: OrganizationUserRequest) => {
    try {
      await organizationUserService.saveUser(
        OrganizationUserRequest.fromJS(values)
      );
      queryClient.invalidateQueries(["organizationUserService.getUsers"]);
      onClose();
    } catch (err: any) {
      if (
        err.data.responseCode === ResponseCode.BusinessRuleFailed &&
        err.data.responseReason === USER_EXISTS_RESPONSE_REASON
      ) {
        return { [FORM_ERROR]: t({ id: "userExistsError" }) };
      }

      return { [FORM_ERROR]: translateError(err, t) };
    }
  };

  const validate = (values: OrganizationUserRequest) => {
    const errors = {} as any;
    if (!isEmail(values.email ?? "")) {
      errors.email = t({ id: "invalidEmail" });
    }
    if (!values.accessLevelId) {
      errors.accessLevelId = t({ id: "required" });
    }
    if (!values.firstName) {
      errors.firstName = t({ id: "required" });
    }
    if (!values.lastName) {
      errors.lastName = t({ id: "required" });
    }
    return errors;
  };

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <DialogTitle sx={styles.title}>
        <T id="addUser" />
      </DialogTitle>
      <Form
        initialValues={init}
        onSubmit={submit}
        validate={validate}
        render={({ handleSubmit, submitting, submitError }) => {
          return (
            <form onSubmit={handleSubmit} noValidate>
              <DialogContent>
                <Grid container spacing={2} justifyContent="center">
                  <Grid item xs={12}>
                    <T id="userWillReceiveEmailInvitation" />
                  </Grid>
                  <Grid item xs={12}>
                    <Field name="firstName" required>
                      {({ input, meta }) => (
                        <TextField
                          {...input}
                          required
                          fullWidth
                          size="small"
                          label={t({ id: "firstName" })}
                          error={meta.error && meta.submitFailed}
                          helperText={
                            meta.error && meta.submitFailed && meta.error
                          }
                        />
                      )}
                    </Field>
                  </Grid>
                  <Grid item xs={12}>
                    <Field name="lastName" required>
                      {({ input, meta }) => (
                        <TextField
                          {...input}
                          required
                          fullWidth
                          size="small"
                          label={t({ id: "lastName" })}
                          error={meta.error && meta.submitFailed}
                          helperText={
                            meta.error && meta.submitFailed && meta.error
                          }
                        />
                      )}
                    </Field>
                  </Grid>
                  <Grid item xs={12}>
                    <Field name="email" required>
                      {({ input, meta }) => (
                        <TextField
                          {...input}
                          required
                          fullWidth
                          size="small"
                          label={t({ id: "email" })}
                          error={meta.error && meta.submitFailed}
                          helperText={
                            meta.error && meta.submitFailed && meta.error
                          }
                        />
                      )}
                    </Field>
                  </Grid>
                  <Grid item xs={12}>
                    <Field name="accessLevelId" required>
                      {({ input, meta }) => (
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <SelectAccessLevel
                            {...input}
                            required
                            textFieldProps={{
                              error: meta.error && meta.submitFailed,
                            }}
                          />
                          {meta.error && meta.submitFailed && (
                            <FormHelperText>{meta.error}</FormHelperText>
                          )}
                        </FormControl>
                      )}
                    </Field>
                    {submitError && (
                      <Alert sx={{ mt: 2, mb: -1 }} severity="error">
                        {submitError}
                      </Alert>
                    )}
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Grid container spacing={2} justifyContent="right">
                  <Grid item xs={12} sm={12} md={2}>
                    <Button onClick={onClose} fullWidth>
                      <T id="cancel" />
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={12} md={2}>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={submitting}
                      fullWidth
                    >
                      {submitting ? (
                        <CircularProgress size={24} />
                      ) : (
                        <T id="invite" />
                      )}
                    </Button>
                  </Grid>
                </Grid>
              </DialogActions>
            </form>
          );
        }}
      />
    </Dialog>
  );
}